import type { ForwardRefExoticComponent, PropsWithChildren } from 'react';
import React, { useCallback, useState, useContext, forwardRef, useRef, useEffect } from 'react';
import { useIntl, defineMessages } from 'react-intl';
import { css } from '@compiled/react';

import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import GlobeIcon from '@atlaskit/icon/core/globe';
import { token } from '@atlaskit/tokens';
import AkTooltip, { TooltipPrimitive } from '@atlaskit/tooltip';
import type { TooltipPrimitiveProps } from '@atlaskit/tooltip';
import Button, { IconButton, SplitButton } from '@atlaskit/button/new';
import { Flex } from '@atlaskit/primitives/compiled';
import DropdownMenu, { DropdownItem, DropdownItemGroup } from '@atlaskit/dropdown-menu';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { fg } from '@confluence/feature-gating';
import { useShortcutStrings } from '@confluence/shortcuts/entry-points/ShortcutVisualizer.ts';
import { ShortcutListener } from '@confluence/shortcuts/entry-points/ShortcutListener.ts';
import { EDITOR2_SHORTCUTS } from '@confluence/shortcuts/entry-points/confluence-shortcuts.ts';
import type { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer.ts';
import { usePageSpaceKey, usePageContentId } from '@confluence/page-context';
import { isCompanyHubSpaceKey } from '@confluence/route-manager/entry-points/companyHubUtils.ts';
import { useIsCompanyHubEnabled } from '@confluence/company-hub-utils/entry-points/useIsCompanyHubEnabled.ts';
import {
	EDITOR_UPDATE_BUTTON_CLICK,
	EDITOR_UPDATE_SHORTCUT_KEY_PRESS,
} from '@confluence/browser-metrics/entry-points/viewPageTransitionSourceConstants.ts';
import { setViewTransitionSource } from '@confluence/browser-metrics/entry-points/viewTransitionSource.ts';
import { PublishHubDialogLoadable } from '@confluence/publish-utils/entry-points/PublishHubDialogLoadable.ts';
import { useIsCurrentPageLive } from '@confluence/live-pages-utils/entry-points/useIsCurrentPageLive.ts';
import { useIsAIEnabledForContent } from '@confluence/ai-common/entry-points/useIsAIEnabledForContent.ts';
import { PreloadButtonWrapper } from '@confluence/editor-features/entry-points/PreloadButtonWrapper.ts';
import { expValEquals } from '@confluence/feature-experiments';
import { useApprovals } from '@confluence/approvals/entry-points/ApprovalsContext.ts';
import { ReplaceApprovalModal } from '@confluence/approvals/entry-points/ReplaceApprovalModal.ts';

import { preloadPublishModal } from './preloadPublishModal.ts';
import { displayPublishErrorFlag } from './publishHelpers.tsx';
import { PublishErrorFlagKeys, SHORTCUT_KEYS } from './constants.ts';
import { usePublishModalActions } from './usePublishModalActions.tsx';
import { i18nShared } from './i18nShared.ts';
import { PublishModalContext } from './PublishModalContext.tsx';

const HOVER_DELAY_MS = 250;

const i18n = defineMessages({
	updateButtonTooltip: {
		id: 'publish-modal.update-button-tooltip',
		description:
			'Tooltip clarifying that the update button allows the user to publish without altering settings with the publish modal',
		defaultMessage: 'Update with current settings',
	},
	updateWithoutNotifyMenuItem: {
		id: 'publish-modal.update-dropdown.update-without-notify',
		description:
			"Text for a menu item under the Update button.  This menu item will update the page and won't send email notifications to other users who are watching the page.",
		defaultMessage: 'Update without notifying watchers',
	},
	adjustSettingsMenuItem: {
		id: 'publish-modal.update-dropdown.adjust-settings',
		description:
			"Text for a menu item under the Update button.  This menu item will open the PublishModal, which allows users to configure their page's settings and then update the page.",
		defaultMessage: 'Adjust update settings',
	},
	updateButtonTooltipCompanyHub: {
		id: 'publish-modal.update-button-tooltip-for-system-space',
		description:
			'Tooltip clarifying that the update button allows the user to publish without altering settings with the publish modal when Company Hub Landing Page is visible',
		defaultMessage: 'Update landing page',
	},
	publishButtonTooltipCompanyHub: {
		id: 'publish-modal.publish-button-tooltip-for-system-space',
		description:
			'Tooltip clarifying that the publish button allows the user to publish without altering settings with the publish modal when Company Hub Landing Page is not visible',
		defaultMessage: 'Publish landing page',
	},
	publishButtonDisabledTooltipCompanyHub: {
		id: 'publish-modal.publish-button-disabled-tooltip-company-hub',
		description:
			'Tooltip to clarify that the publish button is disabled for non-admins of Company Hub and that the changes are saved',
		defaultMessage: 'Only admins can publish at this time - your changes have been saved.',
	},
	publishButtonCompanyHub: {
		id: 'publish-modal.publish-button-for-system-space',
		description:
			'Button for user to publish the page without bringing up the publish modal when Company Hub Landing Page is not visible',
		defaultMessage: 'Publish',
	},
});

export type UpdateSplitButtonProps = {
	publishButtonDisabled: boolean | undefined;
	shortcutIsEnabled: boolean;
	openModal: (() => void) | undefined;
	saveDraft: (() => void) | undefined;
	flags: FlagsStateContainer;
	savingInProgress: boolean;
	shouldShowWorldIcon: boolean;
	contentType?: string;
	isUnpublishedDraft: boolean;
	onHoverPublish?: () => void;
};

const tooltipCustomizationComponentStyle = css({
	background: token('color.background.neutral.bold'),
	color: token('color.text.inverse'),
	boxSizing: 'border-box',
	maxWidth: '300px',
	padding: `${token('space.025')} ${token('space.075')}`,
	top: 0,
	left: 0,

	// eslint-disable-next-line @atlaskit/design-system/no-unsafe-design-token-usage
	borderRadius: token('radius.small', '3px'),
	font: token('font.body.small'),
	overflowWrap: 'break-word',
	wordWrap: 'break-word',
});

const TooltipCustomizationComponent = forwardRef<HTMLDivElement, TooltipPrimitiveProps>(
	function TooltipCustomizationComponent({ children, ...props }, ref) {
		return (
			<TooltipPrimitive
				{...props}
				ref={ref}
				data-testid="tooltipcustomization"
				css={tooltipCustomizationComponentStyle}
			>
				{children}
			</TooltipPrimitive>
		);
	},
) as ForwardRefExoticComponent<TooltipPrimitiveProps>;

const ButtonWrapper = ({
	openModal,
	publishButtonDisabled,
	children,
}: PropsWithChildren<{ openModal?: () => void; publishButtonDisabled: boolean }>) =>
	openModal ? (
		<SplitButton appearance="primary" isDisabled={publishButtonDisabled}>
			{children}
		</SplitButton>
	) : (
		<>{children}</>
	);

export const UpdateSplitButton = ({
	publishButtonDisabled,
	shortcutIsEnabled,
	openModal,
	saveDraft,
	flags,
	savingInProgress,
	shouldShowWorldIcon,
	contentType,
	isUnpublishedDraft,
	onHoverPublish,
}: UpdateSplitButtonProps) => {
	const [isPublishHubOpen, setIsPublishHubOpen] = useState<boolean>(false);
	const [isReplaceApprovalModalOpen, setIsReplaceApprovalModalOpen] = useState<boolean>(false);
	const [pendingPublishArgs, setPendingPublishArgs] = useState<{
		source: string;
		analyticsSource?: string;
		shouldNotifyOverride?: boolean;
	} | null>(null);
	const intl = useIntl();

	const [spaceKey] = usePageSpaceKey();

	const { handlePublish } = usePublishModalActions();

	const publishShortcutStrings = useShortcutStrings({ shortcut: EDITOR2_SHORTCUTS.PUBLISH });

	const isCompanyHub = isCompanyHubSpaceKey(spaceKey);
	const { canPublish, isCompanyHubPublished } = useIsCompanyHubEnabled();
	const isCompanyHubPublishDisabled = isCompanyHub && !isCompanyHubPublished && !canPublish;
	publishButtonDisabled ||= isCompanyHubPublishDisabled;

	const isLivePage = useIsCurrentPageLive();
	const { retrieveEditorTitleAndContent } = useContext(PublishModalContext);

	const { approvalRequest, otherModeReviewStatus } = useApprovals();

	const shouldShowReplaceApprovalModal = useCallback(() => {
		if (!expValEquals('confluence_approvals_eap', 'isEnabled', true)) {
			return false;
		}

		// Exception: draft was explicitly approved and view mode is not pending — this is the
		// expected publish-after-approval flow, so don't warn the user.
		const isDraftApproved = approvalRequest?.review?.reviewStatus === 'APPROVED';
		const isViewModePending = otherModeReviewStatus === 'PENDING';
		if (isDraftApproved && !isViewModePending) {
			return false;
		}

		return otherModeReviewStatus === 'PENDING' || otherModeReviewStatus === 'APPROVED';
	}, [approvalRequest?.review?.reviewStatus, otherModeReviewStatus]);

	const [contentId] = usePageContentId();
	const { isAIEnabledForContent } = useIsAIEnabledForContent({ contentId });

	const publishWithAnalytics = useCallback(
		async (source = 'shortcut', analyticsSource = 'UpdateSplitButton', shouldNotifyOverride?) => {
			const trigger =
				source !== 'button' ? EDITOR_UPDATE_SHORTCUT_KEY_PRESS : EDITOR_UPDATE_BUTTON_CLICK;

			if (contentType && contentType === 'page') {
				setViewTransitionSource(trigger);
			}
			const { title } = await retrieveEditorTitleAndContent();
			const isTitleEmpty = !title;

			// This check is necessary bc the publish API does not throw an error for empty title, so we need to handle this case
			if (isTitleEmpty) {
				await displayPublishErrorFlag({
					isTitleEmpty,
					validationMessage: PublishErrorFlagKeys.TITLE_IS_BLANK,
					flags,
					formatMessage: intl.formatMessage,
					publish: () =>
						publishWithAnalytics(source, 'UpdateButtonTitleGenFlag', shouldNotifyOverride),
					isAIEnabledForContent,
					isLivePage,
				});
			} else {
				if (isCompanyHub && !isCompanyHubPublished) {
					setIsPublishHubOpen(true);
				} else {
					void handlePublish({ analyticsSource, shouldNotifyOverride });
				}
			}
		},
		[
			flags,
			intl.formatMessage,
			handlePublish,
			contentType,
			isCompanyHub,
			isCompanyHubPublished,
			isAIEnabledForContent,
			retrieveEditorTitleAndContent,
			isLivePage,
		],
	);

	const openReplaceApprovalModal = useCallback(
		(source: string, analyticsSource?: string, shouldNotifyOverride?: boolean) => {
			setPendingPublishArgs({ source, analyticsSource, shouldNotifyOverride });
			setIsReplaceApprovalModalOpen(true);
		},
		[],
	);

	const onReplaceApprovalConfirm = useCallback(() => {
		setIsReplaceApprovalModalOpen(false);
		if (pendingPublishArgs) {
			void publishWithAnalytics(
				pendingPublishArgs.source,
				pendingPublishArgs.analyticsSource,
				pendingPublishArgs.shouldNotifyOverride,
			);
			setPendingPublishArgs(null);
		}
	}, [pendingPublishArgs, publishWithAnalytics]);

	const onReplaceApprovalClose = useCallback(() => {
		setIsReplaceApprovalModalOpen(false);
		setPendingPublishArgs(null);
	}, []);

	const onPublishButtonClick = async () => {
		if (
			shouldShowReplaceApprovalModal() &&
			expValEquals('confluence_approvals_eap', 'isEnabled', true)
		) {
			openReplaceApprovalModal('button');
		} else {
			await publishWithAnalytics('button');
		}
	};
	const onPublishWithoutNotifyClick = async () => {
		if (
			shouldShowReplaceApprovalModal() &&
			expValEquals('confluence_approvals_eap', 'isEnabled', true)
		) {
			openReplaceApprovalModal('button', undefined, false);
		} else {
			await publishWithAnalytics('button', undefined, false);
		}
	};

	const hoverTimeoutRef = useRef<number | null>(null);
	useEffect(() => {
		// cleanup timeout on unmount
		return () => {
			if (hoverTimeoutRef.current) {
				window.clearTimeout(hoverTimeoutRef.current);
			}
		};
	}, []);

	const onMouseEnterUpdate = useCallback(() => {
		if (hoverTimeoutRef.current) {
			window.clearTimeout(hoverTimeoutRef.current);
		}
		hoverTimeoutRef.current = window.setTimeout(() => {
			onHoverPublish?.();
		}, HOVER_DELAY_MS);
	}, [onHoverPublish]);

	const onMouseLeaveUpdate = useCallback(() => {
		if (hoverTimeoutRef.current) {
			window.clearTimeout(hoverTimeoutRef.current);
		}
	}, []);

	const buttonTooltipText = () => {
		if (isCompanyHub) {
			if (isCompanyHubPublishDisabled) {
				return intl.formatMessage(i18n.publishButtonDisabledTooltipCompanyHub);
			}
			return isCompanyHubPublished
				? intl.formatMessage(i18n.updateButtonTooltipCompanyHub)
				: intl.formatMessage(i18n.publishButtonTooltipCompanyHub);
		}
		return intl.formatMessage(i18n.updateButtonTooltip);
	};

	const buttonText = () => {
		if (isCompanyHub && !isCompanyHubPublished) {
			return intl.formatMessage(i18n.publishButtonCompanyHub);
		}
		return intl.formatMessage(i18nShared.updateButton);
	};

	const WorldIcon = () => (
		<GlobeIcon
			label={intl.formatMessage(i18nShared.updatePublicContentLabel)}
			color={token('color.icon.inverse')}
		/>
	);

	const { createAnalyticsEvent } = useAnalyticsEvents();

	const chevronButton = (extraProps) => (
		<AkTooltip
			content={intl.formatMessage(
				fg('confluence_frontend_content_wrapper')
					? i18nShared.chevronButtonTooltipObjectHeader
					: i18nShared.chevronButtonTooltip,
			)}
		>
			<IconButton
				{...extraProps}
				isDisabled={publishButtonDisabled}
				icon={(iconProps) => <ChevronDownIcon {...iconProps} size="small" />}
				label={intl.formatMessage(
					fg('confluence_frontend_content_wrapper')
						? i18nShared.chevronButtonTooltipObjectHeader
						: i18nShared.chevronButtonTooltip,
				)}
				testId="publish-modal-chevron-button"
				onMouseEnter={() => preloadPublishModal(saveDraft)}
				onFocus={() => preloadPublishModal(saveDraft)}
				onClick={(e) => {
					extraProps.onClick(e);
					if (fg('confluence_frontend_content_wrapper')) {
						createAnalyticsEvent({
							type: 'sendUIEvent',
							data: {
								action: 'clicked',
								actionSubject: 'button',
								actionSubjectId: 'PublishModalChevronButton',
								source: 'UpdateSplitButton',
							},
						}).fire();
					}
				}}
			/>
		</AkTooltip>
	);

	return (
		<Flex testId="update-split-container">
			{shortcutIsEnabled && !publishButtonDisabled && (
				<ShortcutListener
					key={SHORTCUT_KEYS.USB1}
					accelerator={EDITOR2_SHORTCUTS.PUBLISH}
					listener={() =>
						shouldShowReplaceApprovalModal() &&
						expValEquals('confluence_approvals_eap', 'isEnabled', true)
							? openReplaceApprovalModal('shortcut')
							: publishWithAnalytics()
					}
				/>
			)}
			<ButtonWrapper openModal={openModal} publishButtonDisabled={publishButtonDisabled}>
				<AkTooltip
					tag="span"
					component={TooltipCustomizationComponent}
					content={buttonTooltipText()}
					shortcut={publishShortcutStrings}
				>
					<PreloadButtonWrapper
						isPublish={false}
						contentId={contentId ?? ''}
						spaceKey={spaceKey ?? ''}
						contentType={contentType ?? ''}
						isUnpublishedDraft={isUnpublishedDraft ?? false}
					>
						<Button
							appearance="primary"
							onMouseEnter={onMouseEnterUpdate}
							onMouseLeave={onMouseLeaveUpdate}
							onClick={onPublishButtonClick}
							isDisabled={publishButtonDisabled}
							// This test ID is used in tests and in experience tracking.  Be sure to update all occurences before changing it.
							testId="publish-modal-update-button"
							iconBefore={shouldShowWorldIcon ? WorldIcon : undefined}
							isLoading={savingInProgress}
							interactionName="publish-page-update"
						>
							{buttonText()}
						</Button>
					</PreloadButtonWrapper>
				</AkTooltip>
				{openModal &&
					(fg('confluence_frontend_content_wrapper') ? (
						<DropdownMenu<HTMLButtonElement>
							placement="bottom-end"
							trigger={({ triggerRef, ...triggerProps }) =>
								chevronButton({
									ref: triggerRef,
									...triggerProps,
								})
							}
						>
							<DropdownItemGroup>
								<DropdownItem onClick={onPublishWithoutNotifyClick}>
									{intl.formatMessage(i18n.updateWithoutNotifyMenuItem)}
								</DropdownItem>
								<DropdownItem onClick={openModal} testId="update-dropdown-item-open-publish-modal">
									{intl.formatMessage(i18n.adjustSettingsMenuItem)}
								</DropdownItem>
							</DropdownItemGroup>
						</DropdownMenu>
					) : (
						chevronButton({
							onClick: openModal,
						})
					))}
			</ButtonWrapper>
			{isPublishHubOpen && (
				<PublishHubDialogLoadable
					onClose={() => setIsPublishHubOpen(false)}
					onPublish={handlePublish}
					savingInProgress={savingInProgress}
				/>
			)}
			{isReplaceApprovalModalOpen &&
				expValEquals('confluence_approvals_eap', 'isEnabled', true) && (
					<ReplaceApprovalModal
						otherModeReviewStatus={otherModeReviewStatus}
						onConfirm={onReplaceApprovalConfirm}
						onClose={onReplaceApprovalClose}
					/>
				)}
		</Flex>
	);
};
